import { useState } from 'react';

import { FormDialog } from '@/components/composed/FormDialog';
import { FormField } from '@/components/composed/FormField';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { notify } from '@/components/composed/notify';
import { ApiError } from '@/lib/api/errors';
import { entityId } from '@/lib/api/ids';
import type { Entity } from '@/lib/api/domains/entities';
import {
  entityRelationshipKinds,
  type RelationshipKind,
} from '../relationship-kinds';
import { useCreateRelationship } from '../api/entity-mutations';

/*
 * Record a relationship other than the automatic servicing one (E02.F04 R14),
 * opened from the both-directions view (R11, AC-91f1fc5a). This organization is
 * the providing party; the operator names the other (receiving) party, the kind
 * from the fixed vocabulary (R10 — services / manages / uses, never `owns`), the
 * effective date, and which of the two parties is entitled to end it (R23).
 *
 * The frontend holds no authorization and no rule: the self-relationship
 * refusal (R24), the third-party-terminator refusal (R23) and the
 * duplicate-in-force refusal (R20) are the server's, surfaced here as field or
 * form errors. Pessimistic (mutate → invalidate), no optimistic update.
 */
export function RecordRelationshipDialog({
  entity,
  open,
  onOpenChange,
}: {
  entity: Entity;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const createRelationship = useCreateRelationship(entity.id);
  const [toEntityId, setToEntityId] = useState('');
  const [kind, setKind] = useState<RelationshipKind>('services');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [entitledToEnd, setEntitledToEnd] = useState<'from' | 'to'>('from');
  const [formError, setFormError] = useState<string | null>(null);

  function reset() {
    setToEntityId('');
    setKind('services');
    setEffectiveDate('');
    setEntitledToEnd('from');
    setFormError(null);
  }

  async function onSubmit() {
    setFormError(null);
    try {
      await createRelationship.mutateAsync({
        fromEntityId: entity.id,
        toEntityId: entityId(toEntityId),
        kind,
        effectiveDate,
        entitledToEndEntityId:
          entitledToEnd === 'from' ? entity.id : entityId(toEntityId),
      });
      notify.success('Relationship recorded.');
      reset();
      onOpenChange(false);
    } catch (error) {
      setFormError(
        error instanceof ApiError
          ? error.message
          : 'Failed to record the relationship.',
      );
      throw error;
    }
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
      title={`Record a relationship for ${entity.legalName}`}
      description={`${entity.legalName} is the providing party.`}
      submitLabel="Record"
      pending={createRelationship.isPending}
      onSubmit={() => void onSubmit()}
    >
      <FormField label="Other party (receiving) id">
        {({ id }) => (
          <Input
            id={id}
            value={toEntityId}
            onChange={(e) => setToEntityId(e.target.value)}
          />
        )}
      </FormField>

      <FormField label="Kind">
        {({ id }) => (
          <Select
            value={kind}
            onValueChange={(v) => setKind(v as RelationshipKind)}
          >
            <SelectTrigger id={id}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {entityRelationshipKinds.map((k) => (
                <SelectItem key={k.value} value={k.value}>
                  {k.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </FormField>

      <FormField label="Effective date">
        {({ id }) => (
          <Input
            id={id}
            type="date"
            value={effectiveDate}
            onChange={(e) => setEffectiveDate(e.target.value)}
          />
        )}
      </FormField>

      <FormField label="Entitled to end">
        {({ id }) => (
          <Select
            value={entitledToEnd}
            onValueChange={(v) => setEntitledToEnd(v as 'from' | 'to')}
          >
            <SelectTrigger id={id}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="from">{entity.legalName}</SelectItem>
              <SelectItem value="to">The other party</SelectItem>
            </SelectContent>
          </Select>
        )}
      </FormField>

      {formError ? (
        <p role="alert" className="text-sm text-destructive">
          {formError}
        </p>
      ) : null}
    </FormDialog>
  );
}
